/**
 * Generates FCP7 XML (xmeml v5) from Previs Pro timeline data.
 *
 * Importable by Premiere Pro and DaVinci Resolve.
 */

import type { TimelineProject, TimelineShot, TimelineTransition } from '@/types/timeline.ts';
import type { NLEAudioEntry } from './mediaExtractor.ts';

// ── Types ──────────────────────────────────────────────────────────────

export interface NLEMediaEntry {
  /** Relative path from the XML file (e.g. "video/1A.jpg") */
  path: string;
  /** MIME type */
  mime: string;
  /** Duration in seconds (for stills, same as shot duration) */
  duration: number;
  /** Pixel width */
  width: number;
  /** Pixel height */
  height: number;
}

export interface NLEBuildOptions {
  project: TimelineProject;
  fps: number;
  width: number;
  height: number;
  /** Map from shot ID → media entry */
  shotMedia: Map<number, NLEMediaEntry>;
  /** Audio clip entries for audio tracks */
  audioEntries?: NLEAudioEntry[];
}

// ── Helpers ────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function secondsToFrames(seconds: number, fps: number): number {
  const frames = Math.round((seconds || 0) * fps);
  return Math.max(frames, 0);
}

function rateBlock(fps: number): string {
  const isNtsc = fps === 23.976 || fps === 29.97;
  const timebase = isNtsc ? Math.round(fps) : fps;
  return `<rate><timebase>${timebase}</timebase><ntsc>${isNtsc ? 'TRUE' : 'FALSE'}</ntsc></rate>`;
}

// ── Scene color → FCP7 marker color name ───────────────────────────────

function hexToFcpColor(hex: string): string {
  // Simple hue-based mapping from hex to nearest FCP7 marker color
  if (!hex) return 'Blue';
  const clean = hex.replace('#', '');
  if (clean.length < 6) return 'Blue';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max - min < 30) return 'Blue'; // near-gray
  let hue = 0;
  if (max === r) hue = ((g - b) / (max - min)) * 60;
  else if (max === g) hue = (2 + (b - r) / (max - min)) * 60;
  else hue = (4 + (r - g) / (max - min)) * 60;
  if (hue < 0) hue += 360;
  // Map hue ranges to FCP7 colors
  if (hue < 15 || hue >= 345) return 'Red';
  if (hue < 45) return 'Orange';
  if (hue < 70) return 'Yellow';
  if (hue < 160) return 'Green';
  if (hue < 200) return 'Cyan';
  if (hue < 260) return 'Blue';
  if (hue < 310) return 'Purple';
  return 'Pink';
}

// ── File element builder ───────────────────────────────────────────────

function isStill(mime: string): boolean {
  return mime.startsWith('image/');
}

function buildFileElement(
  fileId: string,
  entry: NLEMediaEntry,
  fps: number,
): string {
  // Stills: file duration = 1 frame; clipitem controls display duration via
  // <stillframe>TRUE</stillframe> + <out>. Video: actual duration in frames.
  const durationFrames = isStill(entry.mime) ? 1 : secondsToFrames(entry.duration, fps);
  return `
          <file id="${esc(fileId)}">
            <name>${esc(entry.path.split('/').pop() ?? '')}</name>
            <pathurl>file:///${esc(entry.path)}</pathurl>
            ${rateBlock(fps)}
            <duration>${durationFrames}</duration>
            <media>
              <video>
                <samplecharacteristics>
                  <width>${entry.width}</width>
                  <height>${entry.height}</height>
                </samplecharacteristics>
              </video>
            </media>
          </file>`;
}

// ── Clip item builder ──────────────────────────────────────────────────

function buildVideoClipItem(
  shot: TimelineShot,
  fileId: string,
  entry: NLEMediaEntry,
  startFrame: number,
  fps: number,
  inlineFile: boolean,
): string {
  const durationFrames = Math.max(secondsToFrames(shot.duration, fps), 1);
  const endFrame = startFrame + durationFrames;
  const still = isStill(entry.mime);
  // Stills: in=0, out=shotDuration (file is 1 frame, stretched by stillframe flag)
  // Video: in=0, out=min(shotDuration, videoDuration) to avoid overrun
  const outFrame = still
    ? durationFrames
    : Math.min(durationFrames, secondsToFrames(entry.duration, fps)) || durationFrames;

  const fileRef = inlineFile
    ? buildFileElement(fileId, entry, fps)
    : `\n              <file id="${esc(fileId)}"/>`;

  const stillTag = still ? '\n              <stillframe>TRUE</stillframe>' : '';

  return `
            <clipitem id="shot_${shot.id}">
              <name>${esc(shot.number + (shot.name ? ' - ' + shot.name : ''))}</name>
              ${rateBlock(fps)}
              <start>${startFrame}</start>
              <end>${endFrame}</end>
              <in>0</in>
              <out>${outFrame}</out>${stillTag}${fileRef}
            </clipitem>`;
}

// ── Marker builder ─────────────────────────────────────────────────────

function buildMarker(
  name: string,
  inFrame: number,
  color: string,
): string {
  return `
          <marker>
            <name>${esc(name)}</name>
            <comment></comment>
            <in>${inFrame}</in>
            <out>-1</out>
            <color>${color}</color>
          </marker>`;
}

// ── Transition builder ────────────────────────────────────────────────

function buildTransitionItem(
  transition: TimelineTransition,
  fps: number,
  boundaryFrame: number,
): string | null {
  if (transition.type === 'cut' || transition.type === 'continuous') return null;
  const durationFrames = secondsToFrames(transition.duration, fps);
  if (durationFrames <= 0) return null;

  // Canonical FCP7 effect identifiers — <effectid> matches <name> verbatim
  // (with spaces), not a CamelCase machine name. DaVinci Resolve only matches
  // the exact strings; the previous "CrossDissolve" / "DipToColorDissolve" form
  // didn't resolve and DaVinci silently fell back to a generic transition that
  // looked the same for fade and dissolve and dropped the front-half of the
  // crossfade.
  let effectName: string;
  if (transition.type === 'dissolve') {
    effectName = 'Cross Dissolve';
  } else {
    // fade_to_black, fade_from_black
    effectName = 'Dip to Color Dissolve';
  }

  // FCP7 XML transitionitems live at their actual timeline position. <start>
  // and <end> are timeline-relative frame indices, centered on the cut between
  // the two adjacent clips (boundaryFrame = the second clip's start = the
  // first clip's end). For odd durations, split half-floor before, half-ceil
  // after so a 15-frame transition at boundary=30 spans 23..38. Previously
  // these were emitted as start=0,end=duration which planted every transition
  // at frame 0 of the sequence, breaking import in DaVinci Resolve and
  // Premiere Pro.
  const halfBefore = Math.floor(durationFrames / 2);
  const halfAfter = durationFrames - halfBefore;
  const startFrame = Math.max(boundaryFrame - halfBefore, 0);
  const endFrame = boundaryFrame + halfAfter;

  // Standard FCP7 wipe parameters — required by DaVinci Resolve to recognize
  // the transition as a real effect rather than a placeholder. wipecode 0 +
  // startratio 0 / endratio 1 describes a linear progression from clip A to
  // clip B over the transition duration, which is what both Cross Dissolve
  // and Dip to Color Dissolve need.
  return `
            <transitionitem>
              <start>${startFrame}</start>
              <end>${endFrame}</end>
              <alignment>center</alignment>
              <effect>
                <name>${effectName}</name>
                <effectid>${effectName}</effectid>
                <effectcategory>Dissolve</effectcategory>
                <effecttype>transition</effecttype>
                <mediatype>video</mediatype>
                <wipecode>0</wipecode>
                <wipeaccuracy>100</wipeaccuracy>
                <startratio>0</startratio>
                <endratio>1</endratio>
                <reverse>FALSE</reverse>
              </effect>
            </transitionitem>`;
}

// ── Audio builders ────────────────────────────────────────────────────

function buildAudioLevelFilter(volume: number, isMuted: boolean): string {
  // FCP7 Audio Levels: 0 = silence, 1 = unity gain (0dB). Linear scale.
  const level = isMuted ? 0 : Math.min(volume, 1);
  return `
                <filter>
                  <effect>
                    <name>Audio Levels</name>
                    <effectid>audiolevels</effectid>
                    <effectcategory>Volume</effectcategory>
                    <effecttype>audiotransition</effecttype>
                    <mediatype>audio</mediatype>
                    <parameter>
                      <name>Level</name>
                      <parameterid>level</parameterid>
                      <valuemin>0</valuemin>
                      <valuemax>1</valuemax>
                      <value>${level}</value>
                    </parameter>
                  </effect>
                </filter>`;
}

function buildAudioClipItem(
  entry: NLEAudioEntry,
  fps: number,
  definedFiles: Set<string>,
): string {
  const durFrames = Math.max(secondsToFrames(entry.duration, fps), 1);
  const startFrame = secondsToFrames(entry.absoluteStart, fps);
  const endFrame = startFrame + durFrames;
  const inFrame = secondsToFrames(entry.bufferOffset, fps);
  const outFrame = inFrame + durFrames;
  const totalDurFrames = outFrame;
  const fileId = `file_audio_${entry.clipId}`;
  const fileName = entry.path.split('/').pop() ?? '';

  // Inline <file> definition on first use, reference on subsequent
  const needsInline = !definedFiles.has(fileId);
  if (needsInline) definedFiles.add(fileId);

  const fileRef = needsInline
    ? `
              <file id="${esc(fileId)}">
                <name>${esc(fileName)}</name>
                <pathurl>file:///${esc(entry.path)}</pathurl>
                ${rateBlock(fps)}
                <duration>${totalDurFrames}</duration>
                <media>
                  <audio>
                    <samplecharacteristics>
                      <samplerate>${entry.mime === 'audio/mpeg' ? 44100 : 48000}</samplerate>
                      <depth>16</depth>
                    </samplecharacteristics>
                    <channelcount>${entry.channels}</channelcount>
                  </audio>
                </media>
              </file>`
    : `\n              <file id="${esc(fileId)}"/>`;

  const volumeFilter = entry.volume < 1.0 || entry.isMuted
    ? buildAudioLevelFilter(entry.volume, entry.isMuted)
    : '';

  return `
            <clipitem id="audio_${entry.clipId}">
              <name>${esc(fileName)}</name>
              ${rateBlock(fps)}
              <start>${startFrame}</start>
              <end>${endFrame}</end>
              <in>${inFrame}</in>
              <out>${outFrame}</out>${fileRef}${volumeFilter}
            </clipitem>`;
}

// Greedy interval-packing: distribute clips across the minimum number of
// non-overlapping sub-tracks. FCP7 XML doesn't allow overlapping clipitems on
// a single <track>, so when a Previs lane has overlapping clips (e.g. two SFX
// loops crossfading at a shot boundary, or any audio that bleeds across
// multiple shots) they silently collide on the same track and DaVinci either
// drops the second one or delays it until the first finishes. Resolve and
// Premiere both support effectively unlimited audio tracks, so we just emit
// one extra <track> per overlap layer instead of trying to mix on a single
// track. Clips are placed in start-time order onto the first sub-track whose
// last clip ends at or before this clip's start; otherwise a new sub-track is
// opened. The result is the minimum number of tracks needed to hold the lane
// without any overlap.
function packClipsIntoSubTracks(
  laneClips: NLEAudioEntry[],
  fps: number,
): NLEAudioEntry[][] {
  const subTracks: { entries: NLEAudioEntry[]; lastEndFrame: number }[] = [];
  for (const entry of laneClips) {
    const startFrame = secondsToFrames(entry.absoluteStart, fps);
    const endFrame = startFrame + Math.max(secondsToFrames(entry.duration, fps), 1);

    let placed = false;
    for (const sub of subTracks) {
      if (sub.lastEndFrame <= startFrame) {
        sub.entries.push(entry);
        sub.lastEndFrame = endFrame;
        placed = true;
        break;
      }
    }
    if (!placed) {
      subTracks.push({ entries: [entry], lastEndFrame: endFrame });
    }
  }
  return subTracks.map(s => s.entries);
}

function buildAudioTracks(
  audioEntries: NLEAudioEntry[],
  fps: number,
): string {
  const lanes = [0, 1, 2, 3]; // Dialog, Music, SFX, VoiceOver
  const tracks: string[] = [];
  const definedAudioFiles = new Set<string>();

  for (const lane of lanes) {
    const laneClips = audioEntries
      .filter(e => e.lane === lane)
      .sort((a, b) => a.absoluteStart - b.absoluteStart);

    const subTracks = packClipsIntoSubTracks(laneClips, fps);

    // Always emit at least one track per lane so the order on the timeline
    // stays Dialog, Music, SFX, VoiceOver even when a lane has no clips.
    if (subTracks.length === 0) {
      tracks.push(`
        <track>
        </track>`);
      continue;
    }

    for (const subTrackClips of subTracks) {
      const items = subTrackClips.map((entry) => buildAudioClipItem(entry, fps, definedAudioFiles));
      tracks.push(`
        <track>${items.join('')}
        </track>`);
    }
  }

  return `
      <audio>${tracks.join('')}
      </audio>`;
}

// ── Main builder ───────────────────────────────────────────────────────

export function buildFCP7XML(opts: NLEBuildOptions): string {
  const { project, fps, width, height, shotMedia, audioEntries } = opts;
  const rate = rateBlock(fps);
  const totalFrames = secondsToFrames(project.totalDuration, fps);
  const seqName = esc(project.title || 'Untitled');

  const clipItems: string[] = [];
  const markers: string[] = [];
  const definedFiles = new Set<string>();

  let currentFrame = 0;

  // Build a flat list of shots in order so we can look ahead for transitions
  const allShots: TimelineShot[] = [];
  for (const scene of project.scenes) {
    if (scene.isOmitted) continue;
    for (const shot of scene.shots) allShots.push(shot);
  }

  let shotIndex = 0;
  for (const scene of project.scenes) {
    if (scene.isOmitted) continue;
    const sceneLabel = `${scene.number} — ${scene.name || scene.locationType + ' ' + scene.timeOfDay}`;
    markers.push(buildMarker(sceneLabel, currentFrame, hexToFcpColor(scene.color)));

    for (const shot of scene.shots) {
      const entry = shotMedia.get(shot.id);
      if (!entry) {
        const durFrames = secondsToFrames(shot.duration, fps);
        clipItems.push(`
            <clipitem id="gap_${shot.id}">
              <name>${esc(shot.number)} (offline)</name>
              ${rate}
              <start>${currentFrame}</start>
              <end>${currentFrame + durFrames}</end>
              <in>0</in>
              <out>${durFrames}</out>
            </clipitem>`);
        currentFrame += durFrames;
        shotIndex++;
        continue;
      }

      const fileId = `file_shot_${shot.id}`;
      const needsInline = !definedFiles.has(fileId);
      if (needsInline) definedFiles.add(fileId);
      clipItems.push(buildVideoClipItem(shot, fileId, entry, currentFrame, fps, needsInline));
      currentFrame += secondsToFrames(shot.duration, fps);

      // Check for transition to next shot. currentFrame is now the cut between
      // this shot and nextShot — pass it as the boundary so the transition is
      // emitted at the correct timeline position.
      const nextShot = allShots[shotIndex + 1];
      if (nextShot) {
        const transition = project.transitions.find(
          t => t.fromShotId === shot.id && t.toShotId === nextShot.id,
        );
        if (transition) {
          const transXml = buildTransitionItem(transition, fps, currentFrame);
          if (transXml) clipItems.push(transXml);
        }
      }
      shotIndex++;
    }
  }

  // Audio tracks
  const audioBlock = audioEntries?.length
    ? buildAudioTracks(audioEntries, fps)
    : '';

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="5">
  <sequence>
    <name>${seqName}</name>
    <duration>${totalFrames}</duration>
    ${rate}
    <media>
      <video>
        <format>
          <samplecharacteristics>
            ${rate}
            <width>${width}</width>
            <height>${height}</height>
            <pixelaspectratio>square</pixelaspectratio>
            <fielddominance>none</fielddominance>
          </samplecharacteristics>
        </format>
        <track>
${clipItems.join('\n')}
        </track>
      </video>${audioBlock}
    </media>
    <timecode>
      ${rate}
      <string>00:00:00:00</string>
      <frame>0</frame>
      <source>source</source>
      <displayformat>NDF</displayformat>
    </timecode>
${markers.join('\n')}
  </sequence>
</xmeml>
`;
}

// ── Resolution from aspect ratio ───────────────────────────────────────

const RESOLUTION_MAP: Record<string, [number, number]> = {
  '16:9': [1920, 1080],
  '9:16': [1080, 1920],
  '2.39:1': [2048, 858],
  '1.85:1': [1998, 1080],
  '4:3': [1440, 1080],
  '1:1': [1080, 1080],
};

export function resolveResolution(aspectRatio: string): { width: number; height: number } {
  const entry = RESOLUTION_MAP[aspectRatio];
  if (entry) return { width: entry[0], height: entry[1] };
  // Fallback: try parsing "W:H" and scale to 1080 height
  const parts = aspectRatio.split(':').map(Number);
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    const h = 1080;
    const w = Math.round((parts[0] / parts[1]) * h);
    return { width: w, height: h };
  }
  return { width: 1920, height: 1080 };
}
